import React, { useMemo } from "react";
import { Connection, Task } from "../../State/Routine/types";
import { AHIcon, Box, useLayoutContext, useTheme } from "arkh";
import Animated, {
  runOnJS,
  SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { getY, LIST_ITEM_HEIGHT, getOrder, Positions } from "./utils";
import { BasicTaskItem } from "../BasicTaskItem";

export function TaskConnectionListItem(props: {
  isSingle: boolean;
  listHeight: Readonly<SharedValue<number>>;
  positions: SharedValue<Positions>;
  task: Task;
  connection?: Connection;
  onReorder: (val: Positions) => void;
}) {
  const layout = useLayoutContext();
  const theme = useTheme();
  const positionY = getY(props.positions.value, props.task.id);
  const y = useSharedValue(positionY);
  const isActive = useSharedValue(false);

  useAnimatedReaction(
    () => props.positions.value[props.task.id]!,
    (newOrder) => {
      if (!isActive.value) {
        const pos = newOrder * LIST_ITEM_HEIGHT;
        y.value = withTiming(pos);
      }
    }
  );

  const ctx = useSharedValue({ y: 0 });

  const gesture = useMemo(
    () =>
      Gesture.Pan()
        .onStart(() => {
          ctx.value.y = getY(props.positions.value, props.task.id);
          y.value = ctx.value.y;
          isActive.value = true;
        })
        .onChange(({ translationY }) => {
          y.value = translationY + ctx.value.y;
          const newOrder = getOrder(y.value);
          const oldOrder = props.positions.value[props.task.id];
          if (newOrder !== oldOrder) {
            const idToSwap = Object.keys(props.positions.value).find(
              (key) => props.positions.value[key] === newOrder
            );
            if (idToSwap) {
              const newPositions = Object.assign({}, props.positions.value);
              newPositions[props.task.id] = newOrder;
              newPositions[idToSwap] = oldOrder;
              props.positions.value = newPositions;
            }
          }
        })
        .onEnd(() => {
          const newPosition = props.positions.value[props.task.id];
          y.value = withTiming(
            newPosition * LIST_ITEM_HEIGHT,
            undefined,
            () => {
              isActive.value = false;
              runOnJS(props.onReorder)(props.positions.value);
            }
          );
        })
        .hitSlop({ top: 20, bottom: 20, left: 90, right: 10 })
        .activeOffsetY(0)
        .minDistance(0),
    [ctx.value, isActive, props.onReorder, props.positions, props.task.id, y]
  );

  const scale = useDerivedValue(() => (isActive.value ? 1.03 : 1));

  const style = useAnimatedStyle(
    () => ({
      transform: [{ translateY: y.value }, { scale: withTiming(scale.value) }],
      height: LIST_ITEM_HEIGHT,
      overflow: "hidden",
      borderColor: theme["border-primary"],
      borderTopWidth: 1,
      borderLeftWidth: 1,
      borderRightWidth: 1,
      borderBottomWidth:
        props.isSingle ||
        y.value > props.listHeight.value - LIST_ITEM_HEIGHT * 1.5
          ? 1
          : 0,
      borderTopColor:
        !isActive.value && y.value > LIST_ITEM_HEIGHT / 2
          ? theme["border-primary"]
          : theme.background,
      borderTopLeftRadius:
        props.isSingle || y.value < LIST_ITEM_HEIGHT / 2
          ? layout.radius.large
          : 0,
      borderTopRightRadius:
        props.isSingle || y.value < LIST_ITEM_HEIGHT / 2
          ? layout.radius.large
          : 0,
      borderBottomLeftRadius:
        props.isSingle ||
        y.value > props.listHeight.value - LIST_ITEM_HEIGHT * 1.5
          ? layout.radius.large
          : 0,
      borderBottomRightRadius:
        props.isSingle ||
        y.value > props.listHeight.value - LIST_ITEM_HEIGHT * 1.5
          ? layout.radius.large
          : 0,
      justifyContent: "center",
      zIndex: isActive.value ? 10 : 2,
      position: "absolute",
      width: "100%",
    }),
    [y, isActive.value, props.listHeight.value]
  );

  return (
    <Animated.View style={style}>
      <Box
        color="surface"
        spaceLeft="medium"
        style={{
          height: LIST_ITEM_HEIGHT,
          flexDirection: "row",
          alignItems: "center",
          position: "relative",
        }}
      >
        <BasicTaskItem task={props.task} />
        {props.isSingle ? null : (
          <GestureDetector gesture={gesture}>
            <Animated.View>
              <AHIcon
                type="drag"
                color="washedText"
                spaceLeft="xxlarge"
                spaceVertical="small"
                spaceRight="medium"
              />
            </Animated.View>
          </GestureDetector>
        )}
      </Box>
    </Animated.View>
  );
}
